import { collection, getDocs, query } from "firebase/firestore";
import { db } from "integrations/firebase/firestore";
import { logError } from "shared/services/ErrorReporting";
import type {
  SeasonalEvent as SeasonalEventDocument,
  SeasonalEventOccurrence as SeasonalEventOccurrenceDocument,
  SeasonalEventPrompt as SeasonalEventPromptDocument,
} from "reedy-data/models";

export const SEASONAL_EVENTS_COLLECTION = "seasonalEvents";

// Stored via model_dump(mode="json"), so dates arrive as ISO strings, not Date.
type IsoDates<T> = {
  [K in keyof T]: T[K] extends Date ? string : T[K];
};

export type SeasonalEventOccurrence = Pick<
  IsoDates<SeasonalEventOccurrenceDocument>,
  "date_kind" | "start" | "end"
>;

export type SeasonalEventPrompt = Pick<
  SeasonalEventPromptDocument,
  "prompt_id" | "text"
>;

export type SeasonalEvent = Pick<
  SeasonalEventDocument,
  "event_id" | "display_name" | "region" | "lead_time_months"
> & {
  occurrences: SeasonalEventOccurrence[];
  prompts: SeasonalEventPrompt[];
};

type StoredSeasonalEvent = Omit<SeasonalEvent, "occurrences" | "prompts"> &
  Partial<Pick<SeasonalEvent, "occurrences" | "prompts">>;

const toReadModel = (document: StoredSeasonalEvent): SeasonalEvent => ({
  event_id: document.event_id,
  display_name: document.display_name,
  region: document.region,
  lead_time_months: document.lead_time_months,
  occurrences: (document.occurrences ?? []).map(
    ({ date_kind, start, end }) => ({ date_kind, start, end }),
  ),
  prompts: (document.prompts ?? []).map(({ prompt_id, text }) => ({
    prompt_id,
    text,
  })),
});

export const fetchSeasonalEvents = async (): Promise<SeasonalEvent[]> => {
  try {
    const snapshot = await getDocs(
      query(collection(db, SEASONAL_EVENTS_COLLECTION)),
    );
    const events: SeasonalEvent[] = [];
    snapshot.forEach((document) => {
      events.push(toReadModel(document.data() as StoredSeasonalEvent));
    });
    return events;
  } catch (error) {
    logError("Error fetching seasonal events:", { originalException: error });
    throw error;
  }
};
